// WrongEnvironmentPopup.tsx

import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import "./WrongEnvironmentPopup.scss";

function isValidWalletAddress(addr: string): boolean {
    return /^0x[a-fA-F0-9]{40}$/.test(addr);
}

export type WrongEnvironmentAdminPayload = {
    adminApiKey: string;
    walletProfile: string;
    worldUsername: string;
};

type Props = {
    open: boolean;
    busy?: boolean;
    onClose: () => void;
    onSubmitAdmin: (
        payload: WrongEnvironmentAdminPayload,
    ) => void | Promise<void>;
    initialWalletProfile?: string | null;
    initialWorldUsername?: string | null;
};

export default function WrongEnvironmentPopup({
    open,
    busy,
    onClose,
    onSubmitAdmin,
    initialWalletProfile,
    initialWorldUsername,
}: Props) {
    const [showAdmin, setShowAdmin] = useState(false);
    const [adminApiKey, setAdminApiKey] = useState("");
    const [walletProfile, setWalletProfile] = useState(
        initialWalletProfile ?? "",
    );
    const [worldUsername, setWorldUsername] = useState(
        initialWorldUsername ?? "",
    );

    const errors = useMemo(() => {
        const e: Record<string, string> = {};
        if (!showAdmin) return e;
        if (!adminApiKey.trim()) e.adminApiKey = "Required";
        if (!walletProfile.trim()) e.walletProfile = "Required";
        else if (!isValidWalletAddress(walletProfile.trim()))
            e.walletProfile = "Invalid wallet address";
        if (!worldUsername.trim()) e.worldUsername = "Required";
        return e;
    }, [adminApiKey, walletProfile, worldUsername, showAdmin]);

    if (!open) return null;

    // Prefer a dedicated modal root if you add one, otherwise fall back to body.
    const mountNode =
        (typeof document !== "undefined" &&
            (document.getElementById("modal-root") || document.body)) ||
        null;

    if (!mountNode) return null;

    return createPortal(
        <div
            className="wrong-environment-popup"
            role="dialog"
            aria-modal="true"
        >
            <div
                className="wrong-environment-popup__backdrop"
                onClick={onClose}
            />

            <div className="wrong-environment-popup__card">
                <div className="wrong-environment-popup__header">
                    <h3>Unsupported environment</h3>
                    <button
                        className="wrong-environment-popup__close"
                        onClick={onClose}
                        aria-label="Close"
                        disabled={!!busy}
                    >
                        ×
                    </button>
                </div>

                <p className="wrong-environment-popup__body">
                    This sign-in flow requires{" "}
                    <strong>World App / MiniKit</strong>. You appear to be
                    running in an unsupported environment.
                </p>

                {!showAdmin ? (
                    <div className="wrong-environment-popup__actions">
                        <button
                            className="wrong-environment-popup__admin-btn"
                            onClick={() => setShowAdmin(true)}
                            disabled={!!busy}
                            type="button"
                        >
                            Admin
                        </button>
                    </div>
                ) : (
                    <form
                        className="wrong-environment-popup__form"
                        onSubmit={async (e) => {
                            e.preventDefault();
                            if (Object.keys(errors).length > 0) return;
                            await onSubmitAdmin({
                                adminApiKey: adminApiKey.trim(),
                                walletProfile: walletProfile.trim(),
                                worldUsername: worldUsername.trim(),
                            });
                        }}
                    >
                        <label>
                            <span>x-admin-api-key</span>
                            <input
                                type="password"
                                autoComplete="off"
                                value={adminApiKey}
                                onChange={(e) => setAdminApiKey(e.target.value)}
                                placeholder="Paste admin key…"
                                disabled={!!busy}
                            />
                            {errors.adminApiKey && (
                                <small className="wrong-environment-popup__error">
                                    {errors.adminApiKey}
                                </small>
                            )}
                        </label>

                        <label>
                            <span>Wallet</span>
                            <input
                                type="text"
                                inputMode="text"
                                autoComplete="off"
                                value={walletProfile}
                                onChange={(e) =>
                                    setWalletProfile(e.target.value)
                                }
                                placeholder="0x…"
                                disabled={!!busy}
                            />
                            {errors.walletProfile && (
                                <small className="wrong-environment-popup__error">
                                    {errors.walletProfile}
                                </small>
                            )}
                        </label>

                        <label>
                            <span>Username</span>
                            <input
                                type="text"
                                autoComplete="off"
                                value={worldUsername}
                                onChange={(e) =>
                                    setWorldUsername(e.target.value)
                                }
                                placeholder="world_username"
                                disabled={!!busy}
                            />
                            {errors.worldUsername && (
                                <small className="wrong-environment-popup__error">
                                    {errors.worldUsername}
                                </small>
                            )}
                        </label>

                        <div className="wrong-environment-popup__actions">
                            <button
                                type="button"
                                className="wrong-environment-popup__secondary"
                                onClick={() => setShowAdmin(false)}
                                disabled={!!busy}
                            >
                                Back
                            </button>
                            <button
                                type="submit"
                                className="wrong-environment-popup__primary"
                                disabled={
                                    !!busy || Object.keys(errors).length > 0
                                }
                            >
                                Continue
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>,
        mountNode,
    );
}
